import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../services/api';
import { UserBalance } from '../types/pool';
import { PaymentCard } from '../components/PaymentCard';

/**
 * Settle Up page (Payment Tracking v2)
 *
 * Everything the user owes across pools, with pay buttons and their pay code.
 * Entry forms redirect here after submitting into a fee pool
 * (location.state.justSubmitted / submittedPool).
 */

const fmtMoney = (n: number) => (n % 1 === 0 ? String(n) : n.toFixed(2));

export function SettleUp() {
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [balance, setBalance] = useState<UserBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navState = (location.state ?? {}) as {
    justSubmitted?: boolean;
    submittedPool?: string;
  };
  const justSubmitted = Boolean(navState.justSubmitted);
  const submittedPool = navState.submittedPool;

  const loadBalance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const data = await api.getMyBalance(token);
      setBalance(data);
    } catch (err) {
      console.error('Failed to load balance:', err);
      setError(err instanceof Error ? err.message : 'Failed to load balance');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your balance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Post-submit confirmation */}
        {justSubmitted && (
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5 mb-6">
            <h2 className="text-xl font-bold text-green-800 m-0">
              ✅ You're in!
            </h2>
            <p className="text-green-700 mt-1 mb-0">
              Your entry{submittedPool ? ` for ${submittedPool}` : ''} is
              submitted and active. One last thing — the entry fee below.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Settle Up</h1>
          <Link to="/pools" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            ← Back to pools
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            {error}
          </div>
        )}

        {balance && balance.totalOwed > 0 ? (
          <>
            {/* Unpaid entries */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Pool</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Entry</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {balance.entries.map((entry) => (
                    <tr key={entry.entryId}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <Link
                          to={`/pools/${entry.poolSlug || entry.poolId}`}
                          className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {entry.poolName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {entry.entryName || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                        ${fmtMoney(entry.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 text-sm font-bold text-gray-900" colSpan={2}>
                      Total
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                      ${fmtMoney(balance.totalOwed)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <PaymentCard balance={balance} />
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
            <p className="text-4xl mb-3">🎉</p>
            <p className="text-lg font-semibold text-gray-900 mb-1">All settled up!</p>
            <p className="text-gray-600 mb-0">You don't owe anything right now.</p>
          </div>
        )}
      </div>
    </div>
  );
}
